type DebugDecisionKind = 'selected' | 'ignored';

interface DebugEvent {
  at: string;
  marker: string;
  details?: Record<string, unknown>;
}

interface DebugHudState {
  selectMode: boolean;
  lastEvent: string;
  lastTap: string;
  lastResolvedTarget: string;
  lastDecision: string;
  selectedCount: number;
}

const DEBUG_QUERY_KEY = 'bugWidgetDebug';
const DEBUG_STORAGE_KEY = 'bugWidgetDebug';
const RING_BUFFER_SIZE = 40;
const WIDGET_ATTR = 'data-br-widget';

const debugBuffer: DebugEvent[] = [];

let debugModeEnabled = false;
let debugHudEl: HTMLDivElement | null = null;
let debugHudState: DebugHudState = {
  selectMode: false,
  lastEvent: '-',
  lastTap: '-',
  lastResolvedTarget: '-',
  lastDecision: '-',
  selectedCount: 0,
};

declare global {
  interface Window {
    __bugWidgetDebugDump?: () => string;
  }
}

function readDebugModeFlag(): boolean {
  const urlEnabled = new URLSearchParams(window.location.search).get(DEBUG_QUERY_KEY) === '1';
  let storageEnabled = false;
  try {
    storageEnabled = window.localStorage.getItem(DEBUG_STORAGE_KEY) === '1';
  } catch {
    storageEnabled = false;
  }
  return urlEnabled || storageEnabled;
}

function renderDebugHud(): void {
  if (!debugModeEnabled || !debugHudEl) return;
  debugHudEl.textContent =
    `BUG DEBUG\n` +
    `selectMode: ${debugHudState.selectMode ? 'ON' : 'OFF'}\n` +
    `last event: ${debugHudState.lastEvent}\n` +
    `last tap: ${debugHudState.lastTap}\n` +
    `last target: ${debugHudState.lastResolvedTarget}\n` +
    `last decision: ${debugHudState.lastDecision}\n` +
    `selected count: ${debugHudState.selectedCount}`;
}

function createDebugHud(): void {
  if (debugHudEl || !debugModeEnabled) return;
  debugHudEl = document.createElement('div');
  debugHudEl.setAttribute(WIDGET_ATTR, '');
  Object.assign(debugHudEl.style, {
    position: 'fixed',
    top: '8px',
    right: '8px',
    zIndex: '2147483646',
    pointerEvents: 'none',
    userSelect: 'none',
    whiteSpace: 'pre',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: '11px',
    lineHeight: '1.3',
    color: '#e4e4e7',
    background: 'rgba(9, 9, 11, 0.82)',
    border: '1px solid rgba(113, 113, 122, 0.55)',
    borderRadius: '6px',
    padding: '8px 10px',
    maxWidth: '280px',
  });
  document.body.appendChild(debugHudEl);
  renderDebugHud();
}

function formatTargetTag(target: Element | null): string {
  if (!target) return '-';
  const tag = target.tagName.toLowerCase();
  const id = target.id ? `#${target.id}` : '';
  const classes = Array.from(target.classList)
    .slice(0, 3)
    .map(c => `.${c}`)
    .join('');
  return `${tag}${id}${classes}` || '-';
}

export function initBugWidgetDebug(): void {
  debugModeEnabled = readDebugModeFlag();
  window.__bugWidgetDebugDump = () => JSON.stringify(debugBuffer, null, 2);
  if (!debugModeEnabled) return;
  createDebugHud();
  logBugWidgetDebug('debug_mode_enabled', { key: DEBUG_QUERY_KEY });
}

export function destroyBugWidgetDebug(): void {
  debugHudEl?.remove();
  debugHudEl = null;
}

export function isBugWidgetDebugEnabled(): boolean {
  return debugModeEnabled;
}

export function logBugWidgetDebug(marker: string, details?: Record<string, unknown>): void {
  if (!debugModeEnabled) return;
  debugBuffer.push({
    at: new Date().toISOString(),
    marker,
    details,
  });
  if (debugBuffer.length > RING_BUFFER_SIZE) {
    debugBuffer.splice(0, debugBuffer.length - RING_BUFFER_SIZE);
  }
}

export function debugSetSelectMode(active: boolean, reason: string): void {
  if (!debugModeEnabled) return;
  debugHudState.selectMode = active;
  logBugWidgetDebug(active ? 'enter_select_mode' : 'exit_or_cancel_select_mode', { reason });
  renderDebugHud();
}

export function debugSetLastEvent(type: string, pointerType?: string): void {
  if (!debugModeEnabled) return;
  debugHudState.lastEvent = pointerType ? `${type} (${pointerType})` : type;
  logBugWidgetDebug('event_handler_hit', { type, pointerType: pointerType ?? '' });
  renderDebugHud();
}

export function debugSetLastTap(x: number, y: number): void {
  if (!debugModeEnabled) return;
  debugHudState.lastTap = `${Math.round(x)}, ${Math.round(y)}`;
  renderDebugHud();
}

export function debugSetLastResolvedTarget(target: Element | null): void {
  if (!debugModeEnabled) return;
  const formatted = formatTargetTag(target);
  debugHudState.lastResolvedTarget = formatted;
  logBugWidgetDebug('target_resolution', { target: formatted });
  renderDebugHud();
}

export function debugLogWidgetGuard(target: Element | null, isWidget: boolean): void {
  if (!debugModeEnabled) return;
  logBugWidgetDebug('is_widget_element_guard', {
    target: formatTargetTag(target),
    result: isWidget,
  });
}

export function debugSetLastDecision(kind: DebugDecisionKind, reason: string): void {
  if (!debugModeEnabled) return;
  debugHudState.lastDecision = `${kind} (${reason})`;
  logBugWidgetDebug('selection_decision', { decision: kind, reason });
  renderDebugHud();
}

export function debugSetSelectedCount(count: number): void {
  if (!debugModeEnabled) return;
  debugHudState.selectedCount = count;
  renderDebugHud();
}
